import { useEffect, useState } from "react";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import type { Tone } from "@/components/patterns";

export type VisitStatus = "PLANNED" | "CHECKED_IN" | "COMPLETED" | "CANCELLED" | "MISSED";
export type LocationFlag = "OK" | "FAR" | "NO_CUSTOMER_LOCATION" | "LOW_ACCURACY";

export interface Visit {
  id: string;
  owner: { id: string; name: string | null };
  related_type: "LEAD" | "ACCOUNT";
  related_id: string;
  related_label: string;
  /** A visit made for a deal (the customer is its account) */
  deal_id: string | null;
  deal_label: string | null;
  /** The deal's scheduled meeting this visit was for */
  task_id: string | null;
  visit_status: VisitStatus;
  planned_at: string | null;
  purpose: string | null;
  check_in_at: string | null;
  check_in_lat: number | null;
  check_in_lng: number | null;
  check_in_accuracy_m: number | null;
  check_in_distance_m: number | null;
  location_flag: LocationFlag | null;
  check_out_at: string | null;
  check_out_lat: number | null;
  check_out_lng: number | null;
  duration_minutes: number | null;
  auto_closed: boolean;
  /** Recorded without signal and sent later (the times are the phone's) */
  offline?: boolean;
  outcome: string | null;
  notes: string | null;
  next_follow_up_at: string | null;
  has_photo: boolean;
  activity_id: string | null;
  created_on: string;
  can_change: boolean;
  /** Only on a check-in reply: the customer has no saved location and this reading may become it */
  can_save_location?: boolean;
}

export interface VisitSettings {
  enabled: boolean;
  radius_m: number;
  min_accuracy_m: number;
}

export const VISIT_STATUS: Record<VisitStatus, { label: string; tone: Tone }> = {
  PLANNED: { label: "Planned", tone: "info" },
  CHECKED_IN: { label: "On site", tone: "warning" },
  COMPLETED: { label: "Done", tone: "success" },
  CANCELLED: { label: "Cancelled", tone: "neutral" },
  MISSED: { label: "Missed", tone: "danger" },
};

export const LOCATION_FLAG: Record<LocationFlag, { label: string; tone: Tone }> = {
  OK: { label: "At the customer", tone: "success" },
  FAR: { label: "Far from customer", tone: "danger" },
  LOW_ACCURACY: { label: "Weak GPS", tone: "warning" },
  NO_CUSTOMER_LOCATION: { label: "No saved location", tone: "neutral" },
};

export const metres = (m: number | null | undefined) => (m == null ? "—" : m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${m} m`);
export const minutes = (n: number | null | undefined) => (n == null ? "—" : n >= 60 ? `${Math.floor(n / 60)} h ${n % 60} min` : `${n} min`);
export const customerLink = (v: Pick<Visit, "related_type" | "related_id">) => `${v.related_type === "LEAD" ? "/leads" : "/accounts"}/${v.related_id}`;

let settingsCache: Promise<VisitSettings | null> | null = null;

/** Whether visits are switched on (and the radius), fetched once per page load */
export function useVisitSettings(allowed: boolean) {
  const [settings, setSettings] = useState<VisitSettings | null>(null);
  useEffect(() => {
    if (!allowed) return;
    settingsCache ??= api
      .get("/api/FieldVisit/Settings")
      .then((r) => unwrapObject<VisitSettings>(r))
      .catch(() => {
        settingsCache = null;
        return null;
      });
    void settingsCache.then(setSettings);
  }, [allowed]);
  return settings;
}

let outcomesCache: Promise<{ value: string; label: string }[]> | null = null;
const OUTCOMES_KEY = "visit-outcomes";
/** Used only before the list has ever loaded on this phone (the same as the default Master lookup rows) */
const DEFAULT_OUTCOMES = [
  { value: "MET_DECISION_MAKER", label: "Met the decision maker" },
  { value: "MET_OTHER", label: "Met someone else" },
  { value: "ORDER_DISCUSSED", label: "Order or requirement discussed" },
  { value: "DEMO_SHOWN", label: "Demo or sample shown" },
  { value: "PAYMENT_COLLECTED", label: "Payment collected" },
  { value: "NOT_AVAILABLE", label: "Not available" },
  { value: "CLOSED", label: "Closed / not found" },
];
const storedOutcomes = (): { value: string; label: string }[] => {
  try {
    const list = JSON.parse(localStorage.getItem(OUTCOMES_KEY) || "null");
    return Array.isArray(list) && list.length ? list : DEFAULT_OUTCOMES;
  } catch {
    return DEFAULT_OUTCOMES;
  }
};

/** The VISIT_OUTCOME list from Master lookups; the last one loaded is kept on the phone, so checking out works without signal */
export function useVisitOutcomes() {
  const [list, setList] = useState<{ value: string; label: string }[]>(storedOutcomes);
  useEffect(() => {
    outcomesCache ??= api
      .get("/api/MasterLookup/ByCategory/VISIT_OUTCOME")
      .then((r) => {
        const rows = unwrapArray<{ code: string; name: string; status?: boolean }>(r).filter((x) => x.status !== false).map((x) => ({ value: x.code, label: x.name }));
        if (rows.length) {
          try {
            localStorage.setItem(OUTCOMES_KEY, JSON.stringify(rows));
          } catch {
            /* storage blocked */
          }
        }
        return rows.length ? rows : storedOutcomes();
      })
      .catch(() => {
        outcomesCache = null;
        return storedOutcomes();
      });
    void outcomesCache.then(setList);
  }, []);
  return list;
}

export interface Position {
  lat: number;
  lng: number;
  accuracy: number;
}

/** The phone's position now (high accuracy, never a cached one), with a message a rep can act on when it fails */
export function getPosition(timeoutMs = 20000): Promise<Position> {
  return new Promise((resolve, reject) => {
    if (!window.isSecureContext) return reject(new Error("Location can't be read on this page. Open the CRM from its usual secure link."));
    if (!navigator.geolocation) return reject(new Error("This browser can't read your location. Use Chrome or Safari on your phone."));
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: Math.round(p.coords.accuracy) }),
      (e) =>
        reject(
          new Error(
            e.code === e.PERMISSION_DENIED
              ? "Location is blocked for this site. Allow location in your browser's site settings, then try again."
              : e.code === e.TIMEOUT
                ? "Your location took too long. Step outside or near a window and try again."
                : "Your location isn't available. Turn on location (GPS) on your phone and try again.",
          ),
        ),
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 0 },
    );
  });
}

/** Shrinks a camera photo to at most 1600 px as a JPEG, so it uploads quickly on mobile data */
export async function shrinkPhoto(file: File, maxSide = 1600): Promise<File> {
  if (!file.type.startsWith("image/")) return file;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext("2d")?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, "image/jpeg", 0.82));
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], file.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
  } catch {
    return file;
  }
}

/** Uploads a visit photo (shrunk first) */
export async function uploadVisitPhoto(visitId: string, file: File) {
  const fd = new FormData();
  fd.append("file", await shrinkPhoto(file));
  return unwrapObject<Visit>(await api.upload(`/api/FieldVisit/Photo/${visitId}`, fd));
}

/** Tells the open-visit bar that a check-in or check-out happened */
export const VISIT_CHANGED = "visit-changed";
export const announceVisitChange = () => window.dispatchEvent(new Event(VISIT_CHANGED));

/** What a timeline entry written by a visit check-out carries */
export interface TimelineVisit {
  visit_id: string;
  has_photo: boolean;
}
